'use client';

import Decimal from 'decimal.js';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { InfoIcon } from '@/lib/components/InfoIcon';
import { PercentText } from '@/lib/components/Text/PercentText';
import { USDText } from '@/lib/components/Text/USDText';
import { useTradingBalances } from '@/lib/components/Trading/TradingDataContext';
import { useMarketPrice } from '@/lib/hooks/useMarketPrice';
import { useMatchingSharesAtPrice } from '@/lib/hooks/useMatchingSharesAtPrice';
import { useFees } from '@/lib/providers/FeesProvider';
import { useOrderFormStore, useSelectedOutcome } from '@/lib/store/order-form-store';
import { useOutcomePrices } from '@/lib/store/prices-store';
import { EMarketAction } from '@/lib/types/market-action';
import { EMarketIntent } from '@/lib/types/market-intent';
import { Dialog, DialogContent, DialogTitle } from '@/lib/ui-kit/dialog';
import { Icon } from '@/lib/ui-kit/icons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/lib/ui-kit/tooltip';
import { cn } from '@/lib/ui-kit/utils';
import { Button } from '@/lib/ui-kit-v2/button';
import { calculateLimitFee } from '@/lib/utils/calculateFees';
import { formatCents, formatMoney } from '@/lib/utils/formatAmount';

interface LimitConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isProcessing?: boolean;
}

type RowProps = {
  label: string;
  tooltip?: string;
  value: React.ReactNode;
  valueClassName?: string;
};

const Row = ({ label, tooltip, value, valueClassName }: RowProps) => (
  <div className="flex h-6 items-center justify-between">
    <div className="flex items-center gap-1">
      <span className="text-[13px] leading-4 tracking-[-0.13px] text-v2-text-primary opacity-50">
        {label}
      </span>
      {tooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="flex items-center">
              <InfoIcon className="ml-0 block opacity-50" />
            </span>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            sideOffset={8}
            className="max-w-[280px] px-2 py-1.5 !text-left text-[12px] leading-4"
          >
            {tooltip}
          </TooltipContent>
        </Tooltip>
      ) : null}
    </div>
    <span
      className={cn(
        'text-[13px] leading-4 tracking-[-0.13px] text-v2-text-primary',
        valueClassName,
      )}
    >
      {value}
    </span>
  </div>
);

export const LimitConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  isProcessing = false,
}: LimitConfirmDialogProps) => {
  const t = useTranslations('trade.confirmDialog');
  const tCommon = useTranslations('common');
  const selectedOutcome = useSelectedOutcome();
  const event = useOrderFormStore.use.event();
  const selectedAction = useOrderFormStore.use.action();
  const intent = useOrderFormStore.use.intent();
  const limitPrice = useOrderFormStore.use.limitPrice();
  const sharesCount = useOrderFormStore.use.sharesCount();
  const selectedOutcomeId = useOrderFormStore.use.selectedOutcomeId();
  const { fees } = useFees();
  const balancesData = useTradingBalances();
  const livePrices = useOutcomePrices(selectedOutcomeId ?? '');

  const isBuy = selectedAction === EMarketAction.BUY;
  const isYes = intent === EMarketIntent.YES;

  // Cost basis of the shares being sold (avgOpenPrice × shares).
  // avgOpenPrice comes from the user's balance — same source as the Open Positions table.
  const sellCostBasis = useMemo(() => {
    if (isBuy || !selectedOutcome) return null;
    const resolution = selectedOutcome.r?.find((r) => r.isPositive === isYes);
    const tokenId = resolution?.id;
    if (!tokenId) return null;
    const balance = balancesData?.balances.find((b) => b.eventTokenId === tokenId);
    if (balance?.averageOpenPrice === undefined) return null;
    return new Decimal(balance.averageOpenPrice).mul(sharesCount);
  }, [isBuy, isYes, selectedOutcome, balancesData, sharesCount]);

  const marketPriceFromHook = useMarketPrice({
    outcomeId: selectedOutcomeId ?? '',
    intent,
    action: selectedAction,
  });

  const currentMarketPrice = useMemo(() => {
    if (isBuy) {
      if (typeof livePrices?.bestAsk === 'number') return new Decimal(livePrices.bestAsk);
      if (typeof livePrices?.bestBid === 'number') return new Decimal(1 - livePrices.bestBid);
    } else {
      if (typeof livePrices?.bestBid === 'number') return new Decimal(livePrices.bestBid);
      if (typeof livePrices?.bestAsk === 'number') return new Decimal(1 - livePrices.bestAsk);
    }
    return marketPriceFromHook ?? new Decimal(0);
  }, [livePrices, marketPriceFromHook, isBuy]);

  const limitPriceUsd = useMemo(() => limitPrice.div(100), [limitPrice]);

  // Shares the book already matches at this price — they fill instantly, as taker.
  const matchingShares = useMatchingSharesAtPrice({
    outcomeId: selectedOutcomeId ?? '',
    intent,
    action: selectedAction,
    price: limitPriceUsd,
  });

  const dialogMode = useMemo(() => {
    // A limit the book covers in full fills immediately, however far from the touch
    // it was priced — the price comparison below never caught that case.
    if (sharesCount.gt(0) && matchingShares.gte(sharesCount)) return 'MARKET_PRICE';
    const priceDifference = limitPriceUsd.minus(currentMarketPrice).abs();
    const isMarketPrice = priceDifference.lt(0.001);
    if (isMarketPrice) return 'MARKET_PRICE';
    const hasPartialMatch = matchingShares.gt(0) && matchingShares.lt(sharesCount);
    if (hasPartialMatch) return 'PART_INSTANTLY';
    return 'REGULAR';
  }, [limitPriceUsd, currentMarketPrice, matchingShares, sharesCount]);

  // Sell: net of the fee withheld on execution (taker on the crossing part).
  const sellFee = useMemo(
    () =>
      fees && !isBuy
        ? calculateLimitFee(limitPriceUsd, sharesCount, matchingShares, EMarketAction.SELL, fees)
        : Decimal(0),
    [fees, isBuy, limitPriceUsd, sharesCount, matchingShares],
  );

  const receivingAmount = useMemo(() => {
    if (isBuy) return sharesCount;
    return limitPriceUsd.mul(sharesCount).minus(sellFee);
  }, [isBuy, limitPriceUsd, sharesCount, sellFee]);

  // Buy: cost is what the user pays now (limit × shares + opening fee).
  // Sell: cost = avgOpenPrice × shares (cost basis of the shares being sold).
  // Falls back to limit × shares when avgOpenPrice is unknown to keep the denominator non-zero.
  const positionValue = useMemo(() => {
    if (isBuy) {
      const totalSpend = limitPriceUsd.mul(sharesCount);
      const openingFee = fees
        ? calculateLimitFee(limitPriceUsd, sharesCount, matchingShares, EMarketAction.BUY, fees)
        : Decimal(0);
      return totalSpend.plus(openingFee);
    }
    return sellCostBasis ?? limitPriceUsd.mul(sharesCount);
  }, [isBuy, limitPriceUsd, sharesCount, matchingShares, fees, sellCostBasis]);

  const potentialReturn = useMemo(() => {
    if (dialogMode !== 'MARKET_PRICE') return Decimal(0);
    if (isBuy) {
      if (sharesCount.isZero() || positionValue.isZero()) return Decimal(0);
      return sharesCount.minus(positionValue);
    }
    if (sharesCount.isZero() || receivingAmount.isZero()) return Decimal(0);
    // Real PnL on execution = proceeds at limit (full-fill assumption) − cost basis.
    if (!sellCostBasis) return Decimal(0);
    return receivingAmount.minus(sellCostBasis);
  }, [dialogMode, isBuy, sharesCount, positionValue, receivingAmount, sellCostBasis]);

  const potentialReturnPercent = useMemo(() => {
    if (positionValue.eq(0)) return Decimal(0);
    return potentialReturn.div(positionValue);
  }, [potentialReturn, positionValue]);

  const remainingShares = useMemo(() => {
    if (dialogMode !== 'PART_INSTANTLY') return Decimal(0);
    return sharesCount.minus(matchingShares);
  }, [dialogMode, sharesCount, matchingShares]);

  const hasMultipleOutcomes = !!event?.markets && event.markets.length > 1;
  const titleText = useMemo(() => {
    if (hasMultipleOutcomes && selectedOutcome?.groupItemTitle) {
      return selectedOutcome.groupItemTitle;
    }
    return `${t('outcome')} ${isYes ? tCommon('yes') : tCommon('no')}`;
  }, [hasMultipleOutcomes, selectedOutcome, isYes, t, tCommon]);

  if (!fees) return null;

  const infoBoxText = (() => {
    if (dialogMode === 'MARKET_PRICE') return t('limitInfoMarketPrice');
    if (dialogMode === 'PART_INSTANTLY') {
      return t('limitInfoPartInstantly', {
        matchingShares: formatMoney(matchingShares.toNumber(), { decimals: 2 }),
        remainingShares: formatMoney(remainingShares.toNumber(), { decimals: 2 }),
      });
    }
    return t('limitInfoRegular', {
      limitPrice: limitPrice.mod(1).eq(0) ? limitPrice.toFixed(0) : limitPrice.toFixed(1),
    });
  })();

  const formatPrice = (value: Decimal) => (value.gt(0) ? formatCents(value) : '--');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="flex w-[400px] max-w-[calc(100vw-32px)] flex-col gap-6 rounded-[16px] border border-v2-surface-tertiary bg-v2-surface-primary p-6"
        showCloseButton={false}
      >
        <DialogTitle className="flex items-center justify-between !pr-0 !text-[24px] !leading-8">
          <span className="flex items-center gap-2">
            <span className="text-[24px] font-semibold leading-8 text-v2-text-primary">
              {titleText}
            </span>
            {hasMultipleOutcomes && (
              <span
                className={cn(
                  'flex items-center justify-center rounded-[8px] px-2 py-0.5 text-[12px] leading-4 tracking-[-0.12px]',
                  isYes ? 'text-v2-green' : 'text-red-400',
                )}
                style={{
                  backgroundColor: isYes ? 'rgba(23, 210, 113, 0.1)' : 'rgba(251, 78, 75, 0.1)',
                }}
              >
                {isYes ? tCommon('yes') : tCommon('no')}
              </span>
            )}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="flex size-10 shrink-0 cursor-pointer items-center justify-center text-v2-text-primary transition-opacity hover:opacity-80"
          >
            <Icon name="Close" size={24} />
          </button>
        </DialogTitle>

        <div className="flex flex-col gap-2">
          {dialogMode === 'MARKET_PRICE' ? (
            <Row
              label={t('estPrice')}
              tooltip={t('estPriceTooltip')}
              value={formatPrice(currentMarketPrice.mul(100))}
            />
          ) : (
            <Row
              label={t('limitPrice')}
              tooltip={t('limitPriceTooltip')}
              value={formatPrice(limitPrice)}
            />
          )}

          {dialogMode === 'MARKET_PRICE' ? (
            <Row
              label={t('estShares')}
              tooltip={isBuy ? t('estSharesBuyTooltip') : t('estSharesSellTooltip')}
              value={
                sharesCount.gt(0) ? formatMoney(sharesCount.toNumber(), { decimals: 2 }) : '--'
              }
            />
          ) : (
            <Row
              label={t('shares')}
              tooltip={isBuy ? t('sharesBuyTooltip') : t('sharesSellTooltip')}
              value={
                sharesCount.gt(0) ? formatMoney(sharesCount.toNumber(), { decimals: 2 }) : '--'
              }
            />
          )}

          {dialogMode === 'MARKET_PRICE' ? (
            <Row
              label={t('potentialReturn')}
              tooltip={isBuy ? t('receivingAmountBuyTooltip') : t('potentialReturnSellTooltip')}
              valueClassName={potentialReturn.gte(0) ? 'text-v2-green' : 'text-red-400'}
              value={
                <>
                  <USDText value={potentialReturn} alwaysShowDecimals /> (
                  <PercentText value={potentialReturnPercent} decimals={0} />)
                </>
              }
            />
          ) : (
            <Row
              label={t('receivingAmount')}
              tooltip={isBuy ? t('receivingAmountBuyTooltip') : t('receivingAmountSellTooltip')}
              valueClassName="text-v2-green"
              value={<USDText value={receivingAmount} alwaysShowDecimals />}
            />
          )}

          <Row
            label={t('positionValue')}
            tooltip={t('positionValueTooltip')}
            value={<USDText value={positionValue} alwaysShowDecimals />}
          />
        </div>

        <div className="flex min-h-[72px] items-center rounded-[10px] bg-v2-neutral-surface p-4">
          <span className="text-[13px] leading-[1.5] text-v2-text-primary">{infoBoxText}</span>
        </div>

        <div className="flex w-full items-start gap-3">
          <Button
            appearance="neutral"
            size="M"
            onClick={onClose}
            disabled={isProcessing}
            className="h-10 flex-1"
          >
            {t('cancel')}
          </Button>
          <Button
            appearance="accent"
            size="M"
            onClick={onConfirm}
            disabled={isProcessing}
            className="h-10 flex-1"
          >
            {isProcessing ? tCommon('loading') : t('confirm')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
